import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useThreadStore } from '../stores/threadStore'
import { useToastStore } from '../stores/toastStore'
import { track } from '../lib/analytics'
import ConfirmDialog from '../components/ConfirmDialog'
import type { UserProfile, SystemPromptPreset } from '../lib/types'
import { TRANSLATIONS } from '../lib/bibleConstants'
const BUILD_SHA = (import.meta.env.VITE_GIT_SHA || 'local').slice(0, 7)

export default function Settings() {
  const { user, updateProfile, logout } = useAuthStore()
  const { presets, fetchPresets, createPreset, updatePreset, deletePreset, fetchModels } = useThreadStore()
  const [saving, setSaving] = useState<string | null>(null)
  const [aiModels, setAiModels] = useState<string[]>([])

  useEffect(() => {
    track('settings_page_visited')
    fetchModels().then((models) => setAiModels(models.map((m) => m.id))).catch(() => {})
  }, [fetchModels])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [editingPreset, setEditingPreset] = useState<{ id: number; name: string; content: string } | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newContent, setNewContent] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<SystemPromptPreset | null>(null)

  useEffect(() => { fetchPresets() }, [fetchPresets])

  if (!user) return null
  const profile = user.profile
  const defaultPresetId = profile.default_system_prompt_preset

  const handleBlur = async (field: keyof UserProfile, value: string) => {
    if (profile[field] === value) return
    setSaving(field)
    await updateProfile({ [field]: value })
    setSaving(null)
    useToastStore.getState().addToast('Settings saved', 'success')
  }

  const handleSetDefault = async (presetId: number) => {
    await updateProfile({ default_system_prompt_preset: presetId } as Partial<UserProfile>)
    track('preset_changed', { preset_id: presetId })
    useToastStore.getState().addToast('Default preset updated', 'success')
  }

  const handleCreate = async () => {
    if (!newName.trim() || !newContent.trim()) return
    await createPreset({ name: newName.trim(), content: newContent.trim() })
    setCreating(false)
    setNewName('')
    setNewContent('')
    useToastStore.getState().addToast('Preset created', 'success')
  }

  const handleSaveEdit = async () => {
    if (!editingPreset) return
    await updatePreset(editingPreset.id, { name: editingPreset.name, content: editingPreset.content })
    setEditingPreset(null)
    useToastStore.getState().addToast('Preset updated', 'success')
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    await deletePreset(deleteTarget.id)
    if (defaultPresetId === deleteTarget.id) {
      const fallback = presets.find((p) => p.is_system_default)
      if (fallback) await updateProfile({ default_system_prompt_preset: fallback.id } as Partial<UserProfile>)
    }
    setDeleteTarget(null)
    setExpandedId(null)
    useToastStore.getState().addToast('Preset deleted', 'success')
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <h1 className="text-xl font-bold">Settings</h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-6">
        <section>
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Profile</h2>
          <div className="space-y-3">
            <Field label="Display Name" value={profile.display_name} field="display_name" onBlur={handleBlur} saving={saving} />
            <TextArea label="Background" value={profile.background} field="background" onBlur={handleBlur} saving={saving} />
            <TextArea label="Study Preferences" value={profile.study_preferences} field="study_preferences" onBlur={handleBlur} saving={saving} />
            <TextArea label="Communication Style" value={profile.communication_style} field="communication_style" onBlur={handleBlur} saving={saving} />
            <Select label="Default Translation" value={profile.default_bible_translation} field="default_bible_translation" options={[...TRANSLATIONS]} onBlur={handleBlur} />
            <Select label="Default AI Model" value={profile.default_ai_model} field="default_ai_model" options={aiModels} onBlur={handleBlur} />
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">System Prompt Presets</h2>
            {!creating && (
              <button
                onClick={() => setCreating(true)}
                className="text-xs text-accent font-medium hover:text-accent-hover transition-colors"
              >
                + New Preset
              </button>
            )}
          </div>

          {creating && (
            <div className="bg-surface rounded-lg p-3 mb-2 space-y-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Preset name"
                className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                autoFocus
              />
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="System prompt content (identity, expertise, tone)"
                rows={4}
                className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => { setCreating(false); setNewName(''); setNewContent('') }}
                  className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newName.trim() || !newContent.trim()}
                  className="px-3 py-1.5 text-xs bg-accent/15 text-accent rounded-lg font-medium hover:bg-accent/25 transition-colors disabled:opacity-40"
                >
                  Create
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {presets.map((p) => {
              const isExpanded = expandedId === p.id
              const isDefault = defaultPresetId === p.id
              const isEditing = editingPreset?.id === p.id

              return (
                <div key={p.id} className="bg-surface rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : p.id)}
                    className="w-full p-3 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{p.name}</span>
                      {isDefault && (
                        <span className="shrink-0 text-[10px] text-accent bg-accent/15 px-1.5 py-0.5 rounded font-medium">
                          Default
                        </span>
                      )}
                      {p.is_system_default && (
                        <span className="shrink-0 text-[10px] text-text-muted bg-elevated px-1.5 py-0.5 rounded">
                          System
                        </span>
                      )}
                    </div>
                    <svg
                      className={`w-4 h-4 text-text-muted shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      {isEditing ? (
                        <>
                          <input
                            value={editingPreset.name}
                            onChange={(e) => setEditingPreset({ ...editingPreset, name: e.target.value })}
                            className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                          />
                          <textarea
                            value={editingPreset.content}
                            onChange={(e) => setEditingPreset({ ...editingPreset, content: e.target.value })}
                            rows={6}
                            className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => setEditingPreset(null)}
                              className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveEdit}
                              className="px-3 py-1.5 text-xs bg-accent/15 text-accent rounded-lg font-medium hover:bg-accent/25 transition-colors"
                            >
                              Save
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-text-secondary whitespace-pre-wrap">{p.content}</p>
                          <div className="flex items-center gap-2 pt-1">
                            {!isDefault && (
                              <button
                                onClick={() => handleSetDefault(p.id)}
                                className="px-3 py-1.5 text-xs bg-accent/15 text-accent rounded-lg font-medium hover:bg-accent/25 transition-colors"
                              >
                                Set as Default
                              </button>
                            )}
                            {!p.is_system_default && (
                              <>
                                <button
                                  onClick={() => setEditingPreset({ id: p.id, name: p.name, content: p.content })}
                                  className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => setDeleteTarget(p)}
                                  className="px-3 py-1.5 text-xs text-error/70 hover:text-error transition-colors"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Account</h2>
          <p className="text-text-secondary mb-3">{user.email}</p>
          <button onClick={logout} className="bg-error/10 text-error px-4 py-2 rounded-lg hover:bg-error/20 transition-colors">
            Log Out
          </button>
        </section>

        <p className="text-center text-text-secondary text-base pt-2">build {BUILD_SHA}</p>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Preset"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function Field({ label, value, field, onBlur, saving }: {
  label: string; value: string; field: keyof UserProfile
  onBlur: (f: keyof UserProfile, v: string) => void; saving: string | null
}) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <div>
      <label className="text-sm text-text-secondary block mb-1">{label} {saving === field && <span className="text-accent text-xs">saving...</span>}</label>
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => onBlur(field, v)}
        className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-accent" />
    </div>
  )
}

function TextArea({ label, value, field, onBlur, saving }: {
  label: string; value: string; field: keyof UserProfile
  onBlur: (f: keyof UserProfile, v: string) => void; saving: string | null
}) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <div>
      <label className="text-sm text-text-secondary block mb-1">{label} {saving === field && <span className="text-accent text-xs">saving...</span>}</label>
      <textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => onBlur(field, v)} rows={3}
        className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-accent resize-none" />
    </div>
  )
}

function Select({ label, value, field, options, onBlur }: {
  label: string; value: string; field: keyof UserProfile
  options: string[]; onBlur: (f: keyof UserProfile, v: string) => void
}) {
  return (
    <div>
      <label className="text-sm text-text-secondary block mb-1">{label}</label>
      <select value={value} onChange={(e) => onBlur(field, e.target.value)}
        className="w-full bg-elevated text-text-primary rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-accent">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}
